import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import Button from "./Button";
import { Share } from "lucide-react";

interface ShareResultsFormProps {
  testResultId: string;
  inviterName: string;
  archetype: string;
  archetypeDescription: string;
  onShare: () => void;
}

const ShareResultsForm: React.FC<ShareResultsFormProps> = ({
  testResultId,
  inviterName,
  archetype,
  archetypeDescription,
  onShare,
}) => {
  const [emails, setEmails] = useState<string[]>(Array(5).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sentEmails, setSentEmails] = useState<string[]>([]);
  const [shareMessage, setShareMessage] = useState<string>("");
  const [showShareNotice, setShowShareNotice] = useState(false);
  const trackGAEvent = (eventName: string, eventParams?: Record<string, any>) => {
    console.log("Tracking GA Event:", eventName, eventParams);
    if (window.gtag) {
      console.log("Call GA Event");
      window.gtag('event', eventName, eventParams || {});
    }
  };

  useEffect(() => {
    const capitalizedArchetype =
      archetype.charAt(0).toUpperCase() + archetype.slice(1);

    setShareMessage(
      `Descubrí que soy un ${capitalizedArchetype} en el Test Latino™. Descubre qué arquetipo eres, tomando el test gratis.`
    );
  }, [archetype]);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  // Update the handleSubmit function
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const validEmails = emails.filter((email) => email.trim() !== "");

      if (validEmails.length === 0) {
        throw new Error("Please enter at least one email address");
      }

      // Check for duplicate emails
      const lowerCased = validEmails.map((e) => e.trim().toLowerCase());
      const hasDuplicates = new Set(lowerCased).size !== lowerCased.length;
      if (hasDuplicates) {
        throw new Error(
          "Duplicate emails are not allowed. Please enter unique email addresses."
        );
      }

      const invalidEmails = validEmails.filter(
        (email) => !validateEmail(email)
      );
      if (invalidEmails.length > 0) {
        throw new Error(
          `Invalid email address${
            invalidEmails.length > 1 ? "es" : ""
          }: ${invalidEmails.join(", ")}`
        );
      }

      // Send emails sequentially with delay to respect rate limits
      const results = [];
      for (let i = 0; i < validEmails.length; i++) {
        const recipientEmail = validEmails[i];

        let shareResultsEmailLink = "";
        switch (archetype.toLowerCase()) {
          case "colibrí":
          case "colibri":
            shareResultsEmailLink = "share-resuls-colibri-es";
            break;
          case "coquí":
          case "coqui":
            shareResultsEmailLink = "share-resuls-coqui-es";
            break;
          case "búho":
          case "buho":
            shareResultsEmailLink = "share-resuls-buho-es";
            break;
          case "quetzal":
            shareResultsEmailLink = "share-resuls-quetzal-es";
            break;
          case "llama":
            shareResultsEmailLink = "share-resuls-llama-es";
            break;
          case "jaguar":
            shareResultsEmailLink = "share-resuls-jaguar-es";
            break;
          case "cóndor":
          case "condor":
            shareResultsEmailLink = "share-resuls-condor-es";
            break;
          default:
            shareResultsEmailLink = "share-results";
            break;
        }

        console.log("archetype.toLowerCase():", archetype.toLowerCase());
        console.log(
          `Invoking function: ${shareResultsEmailLink} with recipient: ${recipientEmail}`
        );

        const { error } = await supabase.functions.invoke(
          shareResultsEmailLink,
          {
            body: {
              testResultId,
              recipientEmail: recipientEmail.trim(),
              inviterName,
              archetype,
              archetypeDescription,
            },
          }
        );

        if (error) throw error;
        results.push(recipientEmail);

        // Add 600ms delay between requests (allows ~1.6 requests per second)
        // Only add delay if there are more emails to send
        if (i < validEmails.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      setSentEmails((prev) => [...prev, ...results]);
      trackGAEvent("share_results", { count: results.length });
      setSuccess(true);
      setEmails(Array(5).fill(""));
      onShare();
    } catch (error) {
      console.error("Error sharing results:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to share results. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);

    // Real-time duplicate check
    const lowerCased = newEmails
      .filter((e) => e.trim() !== "")
      .map((e) => e.trim().toLowerCase());
    const hasDuplicates = new Set(lowerCased).size !== lowerCased.length;
    if (hasDuplicates) {
      setError(
        "Duplicate emails are not allowed. Please enter unique email addresses."
      );
    } else {
      setError(null);
    }
  };

  // Prepare social sharing content
  // const shareUrl = window.location.origin;
  const shareUrl = "https://testlatino.com";
  const capitalizedArchetype =
    archetype.charAt(0).toUpperCase() + archetype.slice(1);

  const shareText = `Descubrí que soy un ${capitalizedArchetype} en el Test Latino™. Descubre qué arquetipo eres, tomando el test gratis.`;

  // LinkedIn sharing URL
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite`;

  // Facebook sharing URL with proper parameters
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    shareUrl
  )}`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    shareText
  )}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-center text-orange-500 mb-4">
        <Share size={24} />
      </div>
      <h3 className="text-xl font-semibold text-center mb-4">
        Comparte tus Resultados
      </h3>
      <p className="text-gray-600 text-center mb-6">
        Comparte tus resultados del <b>Test Latino™</b> con hasta 5 colegas, mentores o amigos.
      </p>

      <form onSubmit={handleSubmit} className="max-w-md mx-auto">
        {emails.map((email, index) => (
          <div key={index} className="mb-4">
            <label
              htmlFor={`email-${index}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Correo Electrónico {index + 1}
            </label>
            <input
              type="email"
              id={`email-${index}`}
              value={email}
              onChange={(e) => handleEmailChange(index, e.target.value)}
              placeholder="Ingresa la dirección de correo"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              disabled={isLoading}
            />
          </div>
        ))}

        {error && (
          <div className="mb-4 text-red-600 text-sm text-center">{error}</div>
        )}

        {success && (
          <div className="mb-4 text-green-600 text-sm text-center">
            ¡Resultados compartidos exitosamente!
          </div>
        )}

        {sentEmails.length > 0 && (
          <div className="mb-4 text-gray-600 text-sm">
            <p>Previamente compartido con:</p>
            <ul className="list-disc pl-5">
              {sentEmails.map((email, index) => (
                <li key={index}>{email}</li>
              ))}
            </ul>
          </div>
        )}

        <Button type="submit" disabled={isLoading} fullWidth>
          {isLoading ? "Intercambiando..." : "Compartir Resultados"}
        </Button>
      </form>

      <div className="mt-8 pt-6 border-t border-gray-200">
        <h4 className="text-lg font-semibold text-center mb-4">
          Compartir en Redes Sociales
        </h4>
        <div className="flex justify-center space-x-4">
          <Button
            variant="outline"
            onClick={() => {
              const linkedInText = shareMessage + " " + shareUrl
              navigator.clipboard.writeText(linkedInText);
              trackGAEvent("share_linkedin", { archetype: capitalizedArchetype });
              // Show the notice
              setShowShareNotice(true);

              // Redirect after 3 seconds
              setTimeout(() => {
                setShowShareNotice(false);
                window.open(linkedinUrl, "_blank", "noopener,noreferrer");
              }, 3000);
            }}
          >
            LinkedIn
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const facebookText = shareMessage + " " + shareUrl
              navigator.clipboard.writeText(facebookText);
              trackGAEvent("share_facebook", { archetype: capitalizedArchetype });
              // Show the notice
              setShowShareNotice(true);

              // Redirect after 3 seconds
              setTimeout(() => {
                setShowShareNotice(false);
                window.open(facebookUrl, "_blank", "noopener,noreferrer");
              }, 3000);
            }}
          >
            Facebook
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const twitterText = shareMessage + " " + shareUrl
              navigator.clipboard.writeText(twitterText);
              // Show the notice
              trackGAEvent("share_twitter", { archetype: capitalizedArchetype });
              setShowShareNotice(true);

              // Redirect after 3 seconds
              setTimeout(() => {
                setShowShareNotice(false);
                window.open(twitterUrl, "_blank", "noopener,noreferrer");
              }, 3000);
            }}
          >
            X (Twitter)
          </Button>
        </div>
      </div>
      {showShareNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              ¡Tus resultados están listos para ser compartidos en las redes sociales!
            </h3>
            <p className="text-gray-600">
              El contenido de tus resultados de la prueba ha sido creado y copiado.
              <br />
              Solo abre (LinkedIn, Facebook), crea una nueva publicación, presiona “pegar” y
              publica.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareResultsForm;
